import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader2, X, User, MapPin,
  Clock, Trash2, LayoutGrid, ListChecks, Cigarette, Check, Printer, Car, Cloud,
  CloudRain, CloudSnow, Sun, CloudSun, Plus,
} from "lucide-react";
import { api } from "../../lib/api";
import { useConfirm } from "./_shared";

/**
 * ScheduleTab — interactive back-office calendar (rewritten Feb 2026).
 *
 * Two view modes toggle in the header:
 *   • Month  — dense colored grid; rentals render as a continuous cyan bar
 *              spanning their start→end days, rides render as amber pills
 *              on their pickup day. Hover a day cell to preview upcoming
 *              reservations without opening the detail modal.
 *   • Agenda — vertical scrolling list, grouped by day, one card per
 *              reservation with the full customer/vehicle/status stack.
 *              Reads better on phones and for busy weeks.
 *
 * Header shows month label + prev/today/next controls; keyboard arrows
 * navigate too. Every card / pill opens the BookingDetailModal, which
 * now has a $200 smoking-fee toggle managers can flip on any booking.
 */
const STATUS_META = {
  pending_approval: { label: "Needs approval", color: "amber", accent: "#fbbf24", bar: "border-l-amber-400" },
  pending_payment:  { label: "Awaiting payment", color: "yellow", accent: "#facc15", bar: "border-l-yellow-400" },
  confirmed:        { label: "Confirmed", color: "lime", accent: "#a3e635", bar: "border-l-lime-400" },
  in_progress:      { label: "In progress", color: "cyan", accent: "#39d6ff", bar: "border-l-cyan" },
  completed:        { label: "Completed", color: "white", accent: "#e2e8f0", bar: "border-l-white/60" },
  cancelled:        { label: "Cancelled", color: "red", accent: "#f87171", bar: "border-l-red-400" },
};
const chipCls = (status) => {
  const s = STATUS_META[status] || STATUS_META.confirmed;
  const map = {
    amber:   "bg-amber-400/15 text-amber-200 ring-amber-400/40",
    yellow:  "bg-yellow-400/15 text-yellow-200 ring-yellow-400/40",
    lime:    "bg-lime-400/15 text-lime-200 ring-lime-400/40",
    cyan:    "bg-cyan/15 text-cyan ring-cyan/40",
    white:   "bg-white/10 text-white/70 ring-white/20",
    red:     "bg-red-500/15 text-red-300 ring-red-500/40",
  };
  return `${map[s.color]} ring-1`;
};

export default function ScheduleTab() {
  const [anchor, setAnchor] = useState(() => new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [view, setView] = useState("month"); // month | agenda | fleet
  // Weather forecast for the anchored month, keyed by YYYY-MM-DD, fetched from
  // Open-Meteo (free, no API key) for the Brooklyn HQ coordinates.
  const weather = useWeather(anchor);
  // Drag-drop: which booking is being dragged, and where to drop it.
  const [dragging, setDragging] = useState(null);
  // Live booking intents — customers actively filling out a booking form.
  // Polled every 15s so staff see ghost pills for in-progress reservations.
  const [intents, setIntents] = useState([]);
  useEffect(() => {
    const load = () => api.get("/admin/booking-intents")
      .then((r) => setIntents(r.data || []))
      .catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  // Full fleet — needed for the manual "New booking" modal so managers can
  // pick any vehicle, not just ones with prior bookings.
  const [fleet, setFleet] = useState([]);
  useEffect(() => {
    api.get("/admin/vehicles")
      .then((r) => setFleet(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // Selected day for the "New booking" modal (YYYY-MM-DD or null).
  const [newBookingDay, setNewBookingDay] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/bookings")
      .then((r) => setBookings(r.data || []))
      .catch(() => toast.error("Failed to load bookings"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  // Prev/next month via keyboard arrows.
  useEffect(() => {
    const onKey = (e) => {
      if (selected) return;
      if (e.key === "ArrowLeft") setAnchor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
      if (e.key === "ArrowRight") setAnchor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
      if (e.key === "t" || e.key === "T") setAnchor(new Date());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  const vehicleOptions = useMemo(() => {
    const map = new Map();
    bookings.forEach((b) => {
      if (b.vehicle_id && !map.has(b.vehicle_id)) map.set(b.vehicle_id, b.vehicle_name || b.vehicle_id);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [bookings]);

  const filtered = useMemo(() => bookings.filter((b) => {
    if (typeFilter !== "all" && b.type !== typeFilter) return false;
    if (vehicleFilter !== "all" && b.vehicle_id !== vehicleFilter) return false;
    return true;
  }), [bookings, typeFilter, vehicleFilter]);

  const { grid, monthLabel } = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const bucketed = useMemo(() => bucketByDay(filtered), [filtered]);
  // Bucket live intents by date-key so the Month view can overlay ghosts.
  const intentsByDay = useMemo(() => {
    const map = {};
    for (const it of intents) {
      const start = it.start_at ? new Date(it.start_at) : null;
      const end = it.end_at ? new Date(it.end_at) : start;
      if (!start) continue;
      const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDay = new Date((end || start).getFullYear(), (end || start).getMonth(), (end || start).getDate());
      while (cursor <= endDay) {
        const key = ymd(cursor);
        (map[key] = map[key] || []).push(it);
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return map;
  }, [intents]);

  const stats = useMemo(() => {
    const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    let rides = 0, rentals = 0, needsApproval = 0, revenue = 0;
    for (const b of filtered) {
      const start = b.type === "rental" ? b.start_at : b.pickup_datetime;
      if (!start) continue;
      const d = new Date(start);
      if (d >= monthStart && d <= monthEnd) {
        if (b.type === "ride") rides++;
        if (b.type === "rental") rentals++;
        if (b.status === "pending_approval") needsApproval++;
        if (b.amount && b.status !== "cancelled") revenue += Number(b.amount);
      }
    }
    return { rides, rentals, needsApproval, revenue };
  }, [filtered, anchor]);

  // Handle a pill drop onto a day cell: PATCH the booking's start_at or
  // pickup_datetime to the new date while preserving the time-of-day.
  const rescheduleTo = useCallback(async (booking, newDate) => {
    if (!booking?.id) return;
    try {
      let patch = {};
      if (booking.type === "ride" && booking.pickup_datetime) {
        const orig = new Date(booking.pickup_datetime);
        const next = new Date(newDate);
        next.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
        patch = { pickup_datetime: next.toISOString() };
      } else if (booking.type === "rental" && booking.start_at && booking.end_at) {
        const s = new Date(booking.start_at);
        const e = new Date(booking.end_at);
        const durMs = e - s;
        const nextStart = new Date(newDate);
        nextStart.setHours(s.getHours(), s.getMinutes(), 0, 0);
        const nextEnd = new Date(nextStart.getTime() + durMs);
        patch = { start_at: nextStart.toISOString(), end_at: nextEnd.toISOString() };
      } else return;
      await api.patch(`/admin/bookings/${booking.id}`, patch);
      toast.success("Rescheduled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reschedule failed");
    }
  }, [load]);

  return (
    <div data-testid="bo-schedule-tab" className="space-y-5">
      {/* Bold gradient header — replaces the tiny label with a proper banner */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10"
        style={{
          background: "linear-gradient(120deg,#071A2F 0%,#0b2540 45%,#1a0b3d 100%)",
        }}>
        <div className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle at 15% 20%, rgba(57,214,255,0.25), transparent 40%), radial-gradient(circle at 85% 80%, rgba(163,230,53,0.18), transparent 45%)",
          }} />
        <div className="relative flex items-center justify-between gap-3 flex-wrap p-6">
          <div>
            <div className="label-caps text-lime-400 text-[10px] tracking-widest">Fleet dispatch</div>
            <h2 className="font-serif text-4xl mt-1 flex items-center gap-3 leading-none">
              <span className="bg-gradient-to-r from-cyan via-lime-300 to-amber-300 bg-clip-text text-transparent">
                {monthLabel}
              </span>
              {loading && <Loader2 size={18} className="animate-spin text-white/40" />}
            </h2>
            <p className="text-white/60 text-xs mt-2 max-w-md">
              Drag any reservation to a new day · ← → to browse months · T for today · click any empty day to book it
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="glass rounded-full p-1 flex gap-1 bg-black/30 backdrop-blur">
              <button type="button" data-testid="bo-cal-view-month"
                onClick={() => setView("month")}
                className={`px-3 py-1.5 rounded-full text-[10px] label-caps inline-flex items-center gap-1 transition ${view === "month" ? "bg-white text-midnight shadow-lg" : "text-white/70 hover:text-white"}`}>
                <LayoutGrid size={11} /> Month
              </button>
              <button type="button" data-testid="bo-cal-view-agenda"
                onClick={() => setView("agenda")}
                className={`px-3 py-1.5 rounded-full text-[10px] label-caps inline-flex items-center gap-1 transition ${view === "agenda" ? "bg-white text-midnight shadow-lg" : "text-white/70 hover:text-white"}`}>
                <ListChecks size={11} /> Agenda
              </button>
              <button type="button" data-testid="bo-cal-view-fleet"
                onClick={() => setView("fleet")}
                className={`px-3 py-1.5 rounded-full text-[10px] label-caps inline-flex items-center gap-1 transition ${view === "fleet" ? "bg-white text-midnight shadow-lg" : "text-white/70 hover:text-white"}`}>
                <Car size={11} /> Fleet week
              </button>
            </div>
            <button type="button" onClick={() => setAnchor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
              data-testid="bo-cal-prev"
              className="p-2.5 rounded-full border border-white/15 hover:border-cyan/50 hover:bg-cyan/10 text-white/80 transition">
              <ChevronLeft size={16} />
            </button>
            <button type="button" onClick={() => setAnchor(new Date())}
              data-testid="bo-cal-today"
              className="px-4 py-2 rounded-full text-xs label-caps bg-lime-400 text-midnight font-semibold hover:brightness-110 transition">
              Today
            </button>
            <button type="button" onClick={() => setAnchor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
              data-testid="bo-cal-next"
              className="p-2.5 rounded-full border border-white/15 hover:border-cyan/50 hover:bg-cyan/10 text-white/80 transition">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Rides this month" value={stats.rides} />
        <Stat label="Rentals this month" value={stats.rentals} />
        <Stat label="Needs approval" value={stats.needsApproval} tone={stats.needsApproval ? "amber" : ""} />
        <Stat label="Booked revenue" value={`$${stats.revenue.toFixed(0)}`} tone="lime" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="glass rounded-full p-1 flex flex-wrap gap-1">
          {[
            { k: "all", l: "All" }, { k: "ride", l: "Rides" }, { k: "rental", l: "Rentals" },
          ].map((f) => (
            <button key={f.k} data-testid={`bo-cal-type-${f.k}`}
              onClick={() => setTypeFilter(f.k)}
              className={`px-3 py-1.5 rounded-full text-[10px] label-caps ${typeFilter === f.k ? "bg-burgundy text-white" : "text-white/60 hover:text-white"}`}>
              {f.l}
            </button>
          ))}
        </div>
        <select value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)}
          data-testid="bo-cal-vehicle-filter"
          className="bg-ink-800 border border-white/10 rounded-full px-4 py-2 text-xs label-caps text-white/70">
          <option value="all">All vehicles ({vehicleOptions.length})</option>
          {vehicleOptions.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
        </select>
        {(typeFilter !== "all" || vehicleFilter !== "all") && (
          <button type="button" onClick={() => { setTypeFilter("all"); setVehicleFilter("all"); }}
            data-testid="bo-cal-filter-reset"
            className="px-3 py-1.5 rounded-full text-[10px] label-caps text-white/60 hover:text-white border border-white/10">
            Reset filters
          </button>
        )}
      </div>

      {view === "month" ? (
        <MonthView grid={grid} anchor={anchor} bucketed={bucketed} onPick={setSelected}
          weather={weather} dragging={dragging} setDragging={setDragging} onReschedule={rescheduleTo}
          intentsByDay={intentsByDay} onQuickAdd={setNewBookingDay} />
      ) : view === "fleet" ? (
        <FleetWeekView bookings={filtered} anchor={anchor} vehicles={vehicleOptions}
          onPick={setSelected} weather={weather} intents={intents} />
      ) : (
        <AgendaView bookings={filtered} anchor={anchor} onPick={setSelected} />
      )}

      {selected && (
        selected.__dayList ? (
          <DayListModal dayKey={selected.__dayKey} items={selected.__dayList}
            onClose={() => setSelected(null)} onPick={(b) => setSelected(b)}
            onQuickAdd={(k) => { setSelected(null); setNewBookingDay(k); }} />
        ) : (
          <BookingDetailModal
            booking={selected}
            onClose={() => setSelected(null)}
            onDeleted={() => { setSelected(null); load(); toast.success("Removed from schedule"); }}
            onSaved={(fresh) => { setSelected(fresh); load(); }}
          />
        )
      )}

      {newBookingDay && (
        <NewBookingModal
          dayKey={newBookingDay}
          fleet={fleet}
          onClose={() => setNewBookingDay(null)}
          onCreated={() => { setNewBookingDay(null); load(); toast.success("Booking added to schedule"); }}
        />
      )}
    </div>
  );
}

/* ---------------- Month view ---------------- */
function MonthView({ grid, anchor, bucketed, onPick, weather, dragging, setDragging, onReschedule, intentsByDay = {}, onQuickAdd }) {
  const [hoverKey, setHoverKey] = useState(null);
  return (
    <>
      <div className="grid grid-cols-7 gap-2 text-[10px] label-caps text-white/40 px-1">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="text-center py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {grid.map((cell) => {
          const key = ymd(cell.date);
          const items = bucketed[key] || [];
          const isToday = sameDay(cell.date, new Date());
          const isPast = cell.date < new Date(new Date().setHours(0,0,0,0));
          const inMonth = cell.date.getMonth() === anchor.getMonth();
          const wx = weather[key];
          const isHover = hoverKey === key && dragging;
          const primaryAccent = items[0] ? (STATUS_META[items[0].booking.status] || STATUS_META.confirmed).accent : null;
          return (
            <div key={key} data-testid={`bo-cal-day-${key}`}
              onDragOver={(e) => { if (dragging) { e.preventDefault(); setHoverKey(key); } }}
              onDragLeave={() => setHoverKey((k) => (k === key ? null : k))}
              onDrop={(e) => {
                e.preventDefault();
                setHoverKey(null);
                if (dragging) { onReschedule(dragging, cell.date); setDragging(null); }
              }}
              className={`group relative min-h-[130px] rounded-2xl p-2 border transition ${
                inMonth ? "bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:border-white/20" : "bg-white/[0.01] border-white/5 opacity-40"
              } ${isToday ? "ring-2 ring-lime-400 shadow-[0_0_28px_rgba(163,230,53,0.25)]" : ""} ${
                isHover ? "ring-2 ring-cyan bg-cyan/[0.1] scale-[1.02]" : ""
              }`}
              style={primaryAccent && items.length > 0 && inMonth ? {
                boxShadow: `inset 3px 0 0 ${primaryAccent}`,
              } : undefined}>
              {/* Day number + weather */}
              <div className="flex items-center justify-between mb-1.5 px-0.5">
                <span className={`text-[15px] font-serif ${isToday ? "text-lime-300 font-bold" : inMonth ? (isPast ? "text-white/40" : "text-white") : "text-white/25"}`}>
                  {cell.date.getDate()}
                </span>
                <div className="flex items-center gap-1">
                  {wx && inMonth && !isPast && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] text-white/50" title={wx.summary}>
                      <WeatherIcon code={wx.code} /> {wx.tempMax != null ? `${Math.round(wx.tempMax)}°` : ""}
                    </span>
                  )}
                  {items.length > 0 && (
                    <span className="text-[9px] label-caps font-bold" style={{ color: primaryAccent || "#94a3b8" }}>{items.length}</span>
                  )}
                  {/* Per-day quick-add "+" — always available (empty or busy), any current-month day.
                      Stops event bubbling so we don't trip drag/onPick. */}
                  {inMonth && !isPast && onQuickAdd && (
                    <button
                      type="button"
                      data-testid={`bo-cal-add-${key}`}
                      title="Add a booking on this day"
                      onClick={(e) => { e.stopPropagation(); onQuickAdd(key); }}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition p-0.5 rounded-md text-cyan/70 hover:text-cyan hover:bg-cyan/10"
                    >
                      <Plus size={11} />
                    </button>
                  )}
                </div>
              </div>
              {/* Event pills */}
              <div className="space-y-1">
                {items.slice(0, 3).map((it) => (
                  <EventPill key={`${it.booking.id}-${it.type}`} item={it}
                    onClick={() => onPick(it.booking)}
                    onDragStart={() => setDragging(it.booking)}
                    onDragEnd={() => setDragging(null)} />
                ))}
                {items.length > 3 && (
                  <button type="button" onClick={() => onPick({ __dayList: items, __dayKey: key })}
                    className="w-full text-left text-[9px] label-caps text-cyan/80 hover:text-cyan px-1 pt-0.5">
                    +{items.length - 3} more
                  </button>
                )}
                {/* Live 'ghost' pills — customers actively filling a booking form for this day */}
                {(intentsByDay[key] || []).map((it) => (
                  <div key={it.session_id}
                    data-testid={`bo-cal-ghost-${it.session_id}`}
                    className="w-full text-left px-1.5 py-1 rounded-md text-[10px] leading-tight truncate border border-dashed border-cyan/60 bg-cyan/10 text-cyan/90 animate-pulse"
                    title={`Customer viewing ${it.vehicle_name || "vehicle"} · stage: ${it.stage || "browsing"}`}>
                    ⌛ {it.vehicle_name?.split(" ").slice(-2).join(" ") || "Someone is booking…"}
                  </div>
                ))}
                {/* Quick-add ghost button — visible on hover of empty future days */}
                {items.length === 0 && !intentsByDay[key] && inMonth && !isPast && onQuickAdd && (
                  <button
                    type="button"
                    onClick={() => onQuickAdd(key)}
                    data-testid={`bo-cal-quickadd-${key}`}
                    className="opacity-0 group-hover:opacity-100 transition inline-flex items-center gap-1 text-[10px] label-caps text-cyan/70 hover:text-cyan px-1"
                  >
                    + Book this day
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ---------------- Fleet week view (swim lanes) ---------------- */
function FleetWeekView({ bookings, anchor, vehicles, onPick, weather, intents = [] }) {
  // Anchor to the Sunday of the anchor's week so the grid is stable.
  const weekStart = useMemo(() => {
    const d = new Date(anchor);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  }, [anchor]);
  const days = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    }), [weekStart]);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  // For each vehicle × day, decide if any booking occupies that cell.
  const cellFor = (vehicleId, day) => {
    const dayStart = new Date(day); dayStart.setHours(0,0,0,0);
    const dayEnd = new Date(day); dayEnd.setHours(23,59,59,999);
    for (const b of bookings) {
      if (b.vehicle_id !== vehicleId) continue;
      if (b.type === "ride" && b.pickup_datetime) {
        const t = new Date(b.pickup_datetime);
        if (t >= dayStart && t <= dayEnd) return { booking: b, kind: "ride" };
      } else if (b.type === "rental" && b.start_at && b.end_at) {
        const s = new Date(b.start_at), e = new Date(b.end_at);
        if (s <= dayEnd && e >= dayStart) {
          const kind = sameDay(s, day) ? "rental_start" : sameDay(e, day) ? "rental_end" : "rental_span";
          return { booking: b, kind };
        }
      }
    }
    return null;
  };
  // Ghost lookup — is a customer currently filling out a booking for this
  // specific vehicle on this specific day? Returns the intent doc so the
  // swim-lane can render a translucent dashed pill above (or instead of)
  // the empty cell.
  const ghostFor = (vehicleId, day) => {
    const dayStart = new Date(day); dayStart.setHours(0,0,0,0);
    const dayEnd = new Date(day); dayEnd.setHours(23,59,59,999);
    for (const it of intents) {
      if (it.vehicle_id !== vehicleId || !it.start_at) continue;
      const s = new Date(it.start_at);
      const e = it.end_at ? new Date(it.end_at) : s;
      if (s <= dayEnd && e >= dayStart) return it;
    }
    return null;
  };
  if (vehicles.length === 0) {
    return <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/50">No vehicles in the fleet yet.</div>;
  }
  return (
    <div data-testid="bo-cal-fleet-week" className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
      <div className="min-w-[900px]">
        {/* Day header row */}
        <div className="grid" style={{ gridTemplateColumns: "160px repeat(7, 1fr)" }}>
          <div className="p-3 text-[10px] label-caps text-white/40 border-b border-r border-white/8">Vehicle</div>
          {days.map((d) => {
            const wx = weather[ymd(d)];
            const isToday = sameDay(d, new Date());
            return (
              <div key={ymd(d)} className={`p-2 text-center border-b border-r border-white/8 ${isToday ? "bg-lime-400/[0.05]" : ""}`}>
                <div className="text-[9px] label-caps text-white/40">{d.toLocaleString(undefined, { weekday: "short" })}</div>
                <div className={`text-sm font-medium ${isToday ? "text-lime-300" : "text-white"}`}>{d.getDate()}</div>
                {wx && (
                  <div className="inline-flex items-center gap-0.5 text-[9px] text-white/40 mt-0.5">
                    <WeatherIcon code={wx.code} /> {wx.tempMax != null ? `${Math.round(wx.tempMax)}°` : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Vehicle rows */}
        {vehicles.map((v) => (
          <div key={v.id} className="grid" style={{ gridTemplateColumns: "160px repeat(7, 1fr)" }}>
            <div className="p-3 border-b border-r border-white/8 flex items-center gap-1.5">
              <Car size={12} className="text-white/40" />
              <span className="text-[11px] text-white truncate">{v.name}</span>
            </div>
            {days.map((d) => {
              const cell = cellFor(v.id, d);
              const ghost = !cell ? ghostFor(v.id, d) : null;
              return (
                <div key={ymd(d)} className="p-1 border-b border-r border-white/8 min-h-[52px]">
                  {cell && (
                    <button type="button" onClick={() => onPick(cell.booking)}
                      data-testid={`bo-fleet-cell-${v.id}-${ymd(d)}`}
                      className={`w-full h-full text-left px-2 py-1 rounded text-[10px] leading-tight truncate hover:brightness-125 transition ${chipCls(cell.booking.status)}`}
                      title={`${cell.booking.full_name || "Customer"} · ${cell.booking.vehicle_name}`}>
                      {cell.kind === "rental_span" ? "▶" : cell.kind === "rental_end" ? "◀" : ""}
                      {cell.booking.type === "ride" ? fmtTime(cell.booking.pickup_datetime) : "Rental"}
                    </button>
                  )}
                  {ghost && (
                    <div
                      data-testid={`bo-fleet-ghost-${v.id}-${ymd(d)}`}
                      className="w-full h-full flex items-center gap-1 px-2 rounded border border-dashed border-cyan/60 bg-cyan/10 text-cyan/90 text-[10px] leading-tight animate-pulse"
                      title={`Customer currently booking · stage: ${ghost.stage || "browsing"}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan animate-ping" />
                      Booking now…
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Agenda view ---------------- */
function AgendaView({ bookings, anchor, onPick }) {
  const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const days = [];
  for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
    days.push(new Date(d));
  }
  const bucketed = useMemo(() => bucketByDay(bookings), [bookings]);
  const withItems = days.filter((d) => (bucketed[ymd(d)] || []).length > 0);
  if (withItems.length === 0) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/50">
        No reservations this month. Use ← / → to jump to another month.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="bo-cal-agenda">
      {withItems.map((day) => {
        const key = ymd(day);
        const items = bucketed[key];
        const isToday = sameDay(day, new Date());
        return (
          <div key={key} data-testid={`bo-agenda-day-${key}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center leading-none ${
                isToday ? "bg-lime-400/20 border border-lime-400/50" : "bg-white/5"
              }`}>
                <div className={`text-[10px] label-caps ${isToday ? "text-lime-300" : "text-white/50"}`}>
                  {day.toLocaleString(undefined, { weekday: "short" })}
                </div>
                <div className={`text-lg font-serif ${isToday ? "text-lime-200" : "text-white"}`}>{day.getDate()}</div>
              </div>
              <div>
                <div className="text-white text-sm font-medium">
                  {day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                </div>
                <div className="text-[11px] text-white/50">{items.length} reservation{items.length === 1 ? "" : "s"}</div>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {items.map((it) => <AgendaCard key={`${it.booking.id}-${it.type}`} item={it} onClick={() => onPick(it.booking)} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AgendaCard({ item, onClick }) {
  const b = item.booking;
  const meta = STATUS_META[b.status] || STATUS_META.confirmed;
  return (
    <button type="button" onClick={onClick} data-testid={`bo-agenda-card-${b.id}`}
      className="text-left rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] p-3 transition group">
      <div className="flex items-center justify-between mb-1.5">
        <span className={`label-caps px-2 py-0.5 rounded-full text-[9px] ${chipCls(b.status)}`}>{meta.label}</span>
        <span className="text-[10px] text-white/50">
          {b.type === "ride" ? fmtTime(b.pickup_datetime) : `${b.days || "?"}d rental`}
        </span>
      </div>
      <div className="font-serif text-base text-white truncate group-hover:text-cyan transition">
        {b.vehicle_name || b.vehicle_id}
      </div>
      <div className="text-[11px] text-white/60 truncate">{b.full_name || "Customer"} · ${Number(b.amount || 0).toFixed(0)}</div>
      {b.smoking_fee_accepted && (
        <div className="mt-1 inline-flex items-center gap-1 text-[9px] text-amber-300 label-caps">
          <Cigarette size={9} /> Smoking fee
        </div>
      )}
    </button>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "lime" ? "text-lime-300" : tone === "amber" ? "text-amber-300" : "text-white";
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
      <div className="label-caps text-[9px] text-white/40">{label}</div>
      <div className={`font-serif text-xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function EventPill({ item, onClick, onDragStart, onDragEnd }) {
  const b = item.booking;
  const meta = STATUS_META[b.status] || STATUS_META.confirmed;
  const isRental = b.type === "rental";
  const label = isRental
    ? (b.vehicle_name?.split(" ").slice(-2).join(" ") || "Rental")
    : `${fmtTime(b.pickup_datetime)} · ${b.vehicle_name?.split(" ").slice(-2).join(" ") || "Ride"}`;
  const isSpan = item.type === "rental_span";
  return (
    <button type="button" onClick={onClick}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`bo-cal-pill-${b.id}`}
      className="w-full text-left px-1.5 py-1 rounded-md text-[10px] leading-tight truncate cursor-grab active:cursor-grabbing hover:brightness-125 hover:scale-[1.02] transition-all"
      style={{
        background: isSpan ? `${meta.accent}22` : `${meta.accent}30`,
        color: meta.accent,
        borderLeft: `2px solid ${meta.accent}`,
        boxShadow: `0 1px 0 ${meta.accent}20`,
      }}
      title={`${b.full_name || "Customer"} · ${b.vehicle_name} — drag to reschedule`}>
      {item.type === "rental_start" ? "▶ " : item.type === "rental_end" ? "◀ " : item.type === "rental_span" ? "▬ " : ""}
      <span className="text-white font-medium">{label}</span>
    </button>
  );
}

/* ---------------- Booking detail modal (with smoking-fee toggle) ---------------- */
function BookingDetailModal({ booking, onClose, onDeleted, onSaved }) {
  const [b, setB] = useState(booking);
  const meta = STATUS_META[b.status] || STATUS_META.confirmed;
  const [busy, setBusy] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const toggleSmoking = async () => {
    const next = !b.smoking_fee_accepted;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/bookings/${b.id}`, { smoking_fee_accepted: next });
      setB(data);
      onSaved?.(data);
      toast.success(next ? "Smoking fee added (+$200)" : "Smoking fee removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    const ok = await confirm({
      title: "Delete this booking?",
      message: "Removes it from the schedule. If it turns out you needed it, you can bring it back from the Trash tab.",
      meta: `${b.full_name || "Customer"} · ${b.vehicle_name}`,
      note: "Audit trail is preserved. Head to Back Office → Trash to restore anything deleted here.",
      confirmLabel: "Yes, delete booking",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/admin/bookings/${b.id}`);
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-center justify-center" onClick={onClose}>
      <div data-testid="bo-cal-detail"
        className="bg-ink-900 border border-white/10 rounded-2xl max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`label-caps px-2 py-0.5 rounded-full text-[10px] ${chipCls(b.status)}`}>{meta.label}</span>
              <span className="label-caps text-[10px] text-white/40">{b.type}</span>
              {b.trip_type && <span className="label-caps text-[10px] text-white/40">{b.trip_type}</span>}
              {b.smoking_fee_accepted && (
                <span className="label-caps px-2 py-0.5 rounded-full text-[10px] bg-amber-400/15 text-amber-300 ring-1 ring-amber-400/40 inline-flex items-center gap-1">
                  <Cigarette size={9} /> Smoking fee
                </span>
              )}
            </div>
            <h3 className="font-serif text-2xl">{b.vehicle_name}</h3>
            <div className="text-white/60 text-sm mt-1 font-mono">{b.id}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
        </div>

        <div className="space-y-2 mb-5">
          <Field icon={<User size={13} />} label={b.full_name} sub={`${b.customer_email || ""} · ${b.customer_phone || ""}`} />
          {b.invoice_number && (
            <div className="text-xs text-amber-200/90 pl-6 -mt-1 inline-flex items-center gap-1"
                 data-testid={`schedule-booking-invoice-link-${b.id}`}>
              <span className="text-white/40">from invoice</span>
              <a href="#invoices"
                 onClick={(e) => {
                   e.preventDefault();
                   window.dispatchEvent(new CustomEvent("bo:jump-to-invoice",
                     { detail: { invoice_id: b.invoice_id,
                                  invoice_number: b.invoice_number } }));
                 }}
                 className="font-mono px-1.5 py-0.5 rounded bg-amber-400/10 border border-amber-400/30 hover:bg-amber-400/20 transition">
                {b.invoice_number}
              </a>
            </div>
          )}
          {b.type === "ride" && (
            <>
              <Field icon={<MapPin size={13} />} label="Pickup" sub={`${b.pickup} · ${fmtFull(b.pickup_datetime)}`} />
              <Field icon={<MapPin size={13} />} label="Dropoff" sub={b.dropoff} />
              <Field icon={<Clock size={13} />} label="Estimated hours" sub={`${b.billed_hours || b.estimated_duration_hours}h`} />
            </>
          )}
          {b.type === "rental" && (
            <>
              <Field icon={<Clock size={13} />} label="Pickup" sub={fmtFull(b.start_at)} />
              <Field icon={<Clock size={13} />} label="Return" sub={fmtFull(b.end_at)} />
              <Field icon={<CalendarIcon size={13} />} label="Duration" sub={`${b.days} day${b.days === 1 ? "" : "s"}`} />
            </>
          )}
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center justify-between mb-3">
          <div>
            <div className="label-caps text-[9px] text-white/40">Total</div>
            <div className="font-serif text-xl text-platinum">${Number(b.amount).toFixed(2)}</div>
          </div>
          {b.type === "ride" && b.deposit_amount != null && (
            <div className="text-right">
              <div className="label-caps text-[9px] text-white/40">Deposit</div>
              <div className="text-lime-300 font-medium">${Number(b.deposit_amount).toFixed(2)}</div>
            </div>
          )}
          {b.type === "rental" && b.deposit != null && (
            <div className="text-right">
              <div className="label-caps text-[9px] text-white/40">Refundable deposit</div>
              <div className="text-lime-300 font-medium">${Number(b.deposit).toFixed(2)}</div>
              {b.deposit_status && (
                <div className="text-[9px] label-caps text-white/40 mt-0.5">{b.deposit_status.replace(/_/g," ")}</div>
              )}
            </div>
          )}
        </div>

        {/* Rental terms editor — the whole reason this modal exists. Managers
            can adjust start/end (which re-derives days), toggle insurance,
            toggle the additional-driver upcharge, and see the deposit
            recompute live. Backend re-prices via `price_rental` on save so
            no drift is possible. */}
        {b.type === "rental" && (
          <RentalTermsEditor rental={b} onSaved={(fresh) => { setB(fresh); onSaved?.(fresh); }} />
        )}

        {/* Smoking-fee toggle — managers can flip $200 on/off from any booking */}
        <button
          type="button"
          data-testid="bo-cal-smoking-toggle"
          onClick={toggleSmoking}
          disabled={busy}
          className={`w-full rounded-xl border p-3 flex items-center justify-between gap-3 transition mb-3 ${
            b.smoking_fee_accepted
              ? "border-amber-400/40 bg-amber-400/[0.06] text-amber-100"
              : "border-white/10 bg-white/[0.03] text-white/70 hover:border-amber-400/30 hover:bg-amber-400/[0.03]"
          } disabled:opacity-40`}>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              b.smoking_fee_accepted ? "bg-amber-400/20 text-amber-300" : "bg-white/5 text-white/50"
            }`}>
              <Cigarette size={14} />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium">
                {b.smoking_fee_accepted ? "Smoking fee applied · $200" : "Add smoking fee to this contract"}
              </div>
              <div className="text-[10px] text-white/50 mt-0.5">
                {b.smoking_fee_accepted ? "Tap to remove — total will drop by $200" : "One-tap upcharge · adds $200 to the total"}
              </div>
            </div>
          </div>
          {b.smoking_fee_accepted ? <Check size={14} className="text-amber-300" /> : <span className="text-[10px] label-caps text-white/50">Add</span>}
        </button>

        <div className="flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 rounded-full py-2.5 text-xs label-caps border border-white/15 text-white/70 hover:text-white hover:border-white/40">
            Close
          </button>
          <button type="button" data-testid="bo-schedule-delete-booking" onClick={del} disabled={busy}
            className="flex-1 rounded-full py-2.5 text-xs label-caps bg-red-500/15 text-red-300 border border-red-500/40 hover:bg-red-500 hover:text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
            <Trash2 size={12} /> {busy ? "…" : "Delete booking"}
          </button>
        </div>
      </div>
      {confirmDialog}
    </div>
  );
}

function DayListModal({ dayKey, items, onClose, onPick, onQuickAdd }) {
  const [y, m, d] = dayKey.split("-").map(Number);
  const dayDate = new Date(y, m - 1, d);
  const label = dayDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-center justify-center" onClick={onClose}>
      <div className="bg-ink-900 border border-white/10 rounded-2xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl">{label} · {items.length} reservations</h3>
          <div className="flex items-center gap-1">
            {onQuickAdd && (
              <button type="button" data-testid="bo-cal-daylist-add"
                onClick={() => onQuickAdd(dayKey)}
                className="p-2 rounded-full hover:bg-cyan/10 text-cyan inline-flex items-center gap-1"
                title="Add another booking on this day">
                <Plus size={14} />
              </button>
            )}
            <button type="button" data-testid="bo-cal-print-manifest"
              onClick={() => printDayManifest(dayDate, items)}
              className="p-2 rounded-full hover:bg-white/5 text-white/70 inline-flex items-center gap-1"
              title="Print daily dispatch manifest">
              <Printer size={14} />
            </button>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
          </div>
        </div>
        <div className="space-y-2 max-h-[70vh] overflow-y-auto">
          {items.map((it) => <AgendaCard key={`${it.booking.id}-${it.type}`} item={it} onClick={() => onPick(it.booking)} />)}
        </div>
      </div>
    </div>
  );
}

/* ---------------- New booking modal (manual add) ---------------- */
/**
 * Compact modal that hits POST /admin/bookings/manual to drop a reservation
 * onto the schedule immediately (status = confirmed, no payment flow).
 * Managers open it from any day cell (+ button) so multiple bookings can be
 * stacked on the same date. Auto-fills the pickup date from the clicked cell.
 */
function NewBookingModal({ dayKey, fleet, onClose, onCreated }) {
  const [y, m, d] = dayKey.split("-").map(Number);
  const dayDate = new Date(y, m - 1, d);
  const label = dayDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  const [type, setType] = useState("ride");            // ride | rental
  const [vehicleId, setVehicleId] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  // Ride
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [pickupTime, setPickupTime] = useState("18:00");
  const [passengerCount, setPassengerCount] = useState(1);
  const [hours, setHours] = useState(3);
  // Rental
  const [endDay, setEndDay] = useState(dayKey);
  const [pickupLocation, setPickupLocation] = useState("");
  const [returnLocation, setReturnLocation] = useState("");
  // Common
  const [amountOverride, setAmountOverride] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentStatus, setPaymentStatus] = useState("pay_at_pickup");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const canSave = !!vehicleId && fullName.trim().length > 1 && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        vehicle_id: vehicleId,
        type,
        full_name: fullName.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        special_requests: notes.trim() || null,
      };
      if (amountOverride) payload.amount = Number(amountOverride);
      if (type === "ride") {
        const [hh, mm] = pickupTime.split(":").map(Number);
        const dt = new Date(y, m - 1, d, hh || 18, mm || 0, 0);
        payload.pickup_datetime = dt.toISOString();
        payload.pickup = pickup.trim() || null;
        payload.dropoff = dropoff.trim() || null;
        payload.passenger_count = Number(passengerCount) || 1;
        payload.estimated_duration_hours = Number(hours) || 3;
      } else {
        const startDt = new Date(y, m - 1, d, 10, 0, 0);
        const [ey, em, ed] = endDay.split("-").map(Number);
        const endDt = new Date(ey, em - 1, ed, 10, 0, 0);
        payload.start_at = startDt.toISOString();
        payload.end_at = endDt.toISOString();
        payload.pickup_location = pickupLocation.trim() || null;
        payload.return_location = returnLocation.trim() || null;
      }
      await api.post("/admin/bookings/manual", payload);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create booking");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-center justify-center overflow-y-auto"
         onClick={onClose}>
      <form
        onSubmit={submit}
        data-testid="bo-new-booking-modal"
        className="bg-ink-900 border border-white/10 rounded-2xl max-w-xl w-full p-5 my-auto max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-caps text-[10px] text-cyan/80">New booking</div>
            <h3 className="font-serif text-xl mt-0.5">{label}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X size={16} /></button>
        </div>

        {/* Type toggle */}
        <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-white/5 border border-white/10 mb-4">
          {["ride", "rental"].map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`bo-new-booking-type-${t}`}
              onClick={() => setType(t)}
              className={`py-2 rounded-full text-xs label-caps tracking-widest transition ${
                type === t ? "bg-cyan text-black font-bold" : "text-white/60 hover:text-white"
              }`}
            >
              {t === "ride" ? "Chauffeur ride" : "Self-drive rental"}
            </button>
          ))}
        </div>

        {/* Vehicle picker */}
        <label className="block mb-3">
          <span className="label-caps text-[9px] text-white/60 tracking-widest">Vehicle</span>
          <select
            data-testid="bo-new-booking-vehicle"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            required
            className="mt-1 w-full bg-black/50 border border-white/15 focus:border-cyan/60 rounded-lg px-3 py-2 text-white text-sm outline-none"
          >
            <option value="">Choose a vehicle…</option>
            {fleet
              .filter((v) => type === "rental"
                ? ["rental", "both"].includes(v.available_for || "chauffeur")
                : ["chauffeur", "both"].includes(v.available_for || "chauffeur"))
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} · {v.category}
                </option>
              ))}
          </select>
        </label>

        {/* Customer */}
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <TextField label="Full name" testid="bo-new-booking-name" value={fullName} onChange={setFullName} required />
          <TextField label="Phone" testid="bo-new-booking-phone" value={phone} onChange={setPhone} />
        </div>
        <TextField label="Email (optional)" testid="bo-new-booking-email" value={email} onChange={setEmail} type="email" className="mb-3" />

        {/* Type-specific */}
        {type === "ride" ? (
          <>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <TextField label="Pickup time" testid="bo-new-booking-time" value={pickupTime} onChange={setPickupTime} type="time" />
              <TextField label="Duration (hrs)" testid="bo-new-booking-hours" value={hours} onChange={setHours} type="number" />
            </div>
            <TextField label="Pickup location" testid="bo-new-booking-pickup" value={pickup} onChange={setPickup} className="mb-3" />
            <TextField label="Drop-off" testid="bo-new-booking-dropoff" value={dropoff} onChange={setDropoff} className="mb-3" />
            <TextField label="Passenger count" testid="bo-new-booking-pax" value={passengerCount} onChange={setPassengerCount} type="number" className="mb-3" />
          </>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <TextField label="Start date" value={dayKey} onChange={() => {}} type="date" readOnly />
              <TextField label="End date" testid="bo-new-booking-end" value={endDay} onChange={setEndDay} type="date" required />
            </div>
            <TextField label="Pickup location" testid="bo-new-booking-pickup-loc" value={pickupLocation} onChange={setPickupLocation} className="mb-3" />
            <TextField label="Return location" testid="bo-new-booking-return-loc" value={returnLocation} onChange={setReturnLocation} className="mb-3" />
          </>
        )}

        {/* Payment */}
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <label className="block">
            <span className="label-caps text-[9px] text-white/60 tracking-widest">Payment method</span>
            <select
              data-testid="bo-new-booking-pm"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="mt-1 w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none"
            >
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="invoice">Invoice</option>
              <option value="manual">Manual</option>
            </select>
          </label>
          <label className="block">
            <span className="label-caps text-[9px] text-white/60 tracking-widest">Status</span>
            <select
              data-testid="bo-new-booking-ps"
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="mt-1 w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none"
            >
              <option value="pay_at_pickup">Pay at pickup</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </label>
          <TextField label="Amount override ($)" testid="bo-new-booking-amount" value={amountOverride} onChange={setAmountOverride} type="number" placeholder="auto" />
        </div>

        <label className="block mb-4">
          <span className="label-caps text-[9px] text-white/60 tracking-widest">Notes (optional)</span>
          <textarea
            data-testid="bo-new-booking-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mt-1 w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none resize-none"
          />
        </label>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-white/70 hover:text-white text-sm">
            Cancel
          </button>
          <button
            type="submit"
            data-testid="bo-new-booking-save"
            disabled={!canSave}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-cyan hover:brightness-110 text-black font-bold label-caps text-xs tracking-widest transition disabled:opacity-50"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add to schedule
          </button>
        </div>
      </form>
    </div>
  );
}

function TextField({ label, testid, value, onChange, type = "text", placeholder, required, readOnly, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-caps text-[9px] text-white/60 tracking-widest">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        readOnly={readOnly}
        className={`mt-1 w-full bg-black/50 border border-white/15 focus:border-cyan/60 rounded-lg px-3 py-2 text-white text-sm outline-none ${readOnly ? "opacity-70" : ""}`}
      />
    </label>
  );
}


function Field({ icon, label, sub }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-white/40">{icon}</span>
      <div>
        <div className="text-white text-sm">{label}</div>
        {sub && <div className="text-white/50 text-xs">{sub}</div>}
      </div>
    </div>
  );
}

/**
 * RentalTermsEditor — inline editor for the terms on a signed rental. Managers
 * frequently need to correct the pickup/return window, remove insurance if
 * the customer chose "own coverage" after signing, or verify the deposit is
 * correct. Every save round-trips through `PATCH /admin/bookings/{id}` where
 * the backend re-runs `price_rental()` — so days, subtotal, tax, insurance
 * lines, deposit, and total all stay perfectly in sync.
 */
function RentalTermsEditor({ rental, onSaved }) {
  const toLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const baselineStart = useMemo(() => toLocal(rental.start_at), [rental.start_at]);
  const baselineEnd   = useMemo(() => toLocal(rental.end_at),   [rental.end_at]);
  const baselineAmount = Number(rental.amount || 0);
  const baselineLocked = !!rental.price_locked;

  const [start, setStart] = useState(baselineStart);
  const [end, setEnd] = useState(baselineEnd);
  const [insurance, setInsurance] = useState(!!rental.insurance_optin);
  const [addlDriver, setAddlDriver] = useState(!!rental.additional_driver_optin);
  const [customPriceOn, setCustomPriceOn] = useState(baselineLocked);
  const [customPrice, setCustomPrice] = useState(baselineAmount);
  const [saving, setSaving] = useState(false);

  const startDirty = start !== baselineStart;
  const endDirty   = end   !== baselineEnd;
  const startISO = startDirty && start ? new Date(start).toISOString() : rental.start_at;
  const endISO   = endDirty   && end   ? new Date(end).toISOString()   : rental.end_at;

  const days = useMemo(() => {
    try {
      const ms = new Date(endISO) - new Date(startISO);
      const raw = Math.max(1, Math.ceil(ms / (24 * 3600 * 1000)));
      return Math.max(raw, rental.days_min || 2);
    } catch { return rental.days || 0; }
  }, [startISO, endISO, rental.days, rental.days_min]);

  const priceDirty = (customPriceOn !== baselineLocked)
    || (customPriceOn && Number(customPrice) !== baselineAmount);
  const dirty =
    startDirty ||
    endDirty   ||
    insurance !== !!rental.insurance_optin ||
    addlDriver !== !!rental.additional_driver_optin ||
    priceDirty;

  const save = async () => {
    if (!dirty || saving) return;
    if (new Date(endISO) <= new Date(startISO)) {
      toast.error("Return must be after pickup");
      return;
    }
    setSaving(true);
    try {
      const patch = {
        insurance_optin: insurance,
        additional_driver_optin: addlDriver,
      };
      if (startDirty) patch.start_at = startISO;
      if (endDirty)   patch.end_at   = endISO;
      // Custom price wins. Backend locks it so future edits don't clobber.
      if (customPriceOn) {
        const n = Number(customPrice);
        if (!Number.isFinite(n) || n < 0) {
          toast.error("Enter a valid price");
          setSaving(false);
          return;
        }
        patch.amount = n;
      } else if (baselineLocked) {
        // User un-checked the override → tell the backend to auto-price again.
        patch.price_locked = false;
      }
      const { data } = await api.patch(`/admin/bookings/${rental.id}`, patch);
      onSaved?.(data);
      toast.success(
        data.price_locked
          ? `Custom price saved · $${Number(data.amount || 0).toFixed(2)} (locked)`
          : `Rental re-priced — ${data.days} day${data.days === 1 ? "" : "s"} · $${Number(data.amount || 0).toFixed(2)}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="bo-cal-rental-editor" className="rounded-2xl border border-cyan/25 bg-cyan/[0.03] p-4 space-y-3 mb-3">
      <div className="label-caps text-[10px] text-cyan flex items-center gap-1.5">
        <CalendarIcon size={11} /> Rental terms · recompute on save
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <label className="block">
          <div className="label-caps text-[9px] text-white/50 mb-1">Pickup</div>
          <input
            type="datetime-local"
            data-testid="bo-cal-rental-start"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="w-full bg-ink-800 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:border-cyan/50 outline-none"
          />
        </label>
        <label className="block">
          <div className="label-caps text-[9px] text-white/50 mb-1">Return</div>
          <input
            type="datetime-local"
            data-testid="bo-cal-rental-end"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="w-full bg-ink-800 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:border-cyan/50 outline-none"
          />
        </label>
      </div>

      <div className="flex items-center justify-between text-[11px] text-white/70">
        <span>Duration</span>
        <span className="text-white">
          {days} day{days === 1 ? "" : "s"}
          {days !== rental.days && <span className="text-cyan ml-1">(was {rental.days})</span>}
        </span>
      </div>

      <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
        <div>
          <div className="text-xs text-white">Plenty Rydez insurance</div>
          <div className="text-[10px] text-white/50">
            {rental.has_own_insurance
              ? "Renter has own policy on file — insurance forced off."
              : `Adds coverage at $${Number(rental.insurance_daily || 75).toFixed(0)}/day.`}
          </div>
        </div>
        <input
          type="checkbox"
          data-testid="bo-cal-rental-insurance"
          checked={insurance && !rental.has_own_insurance}
          disabled={!!rental.has_own_insurance}
          onChange={(e) => setInsurance(e.target.checked)}
          className="w-5 h-5 accent-cyan"
        />
      </label>

      <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
        <div>
          <div className="text-xs text-white">Additional named driver</div>
          <div className="text-[10px] text-white/50">Adds $10/day when checked.</div>
        </div>
        <input
          type="checkbox"
          data-testid="bo-cal-rental-addl-driver"
          checked={addlDriver}
          onChange={(e) => setAddlDriver(e.target.checked)}
          className="w-5 h-5 accent-cyan"
        />
      </label>

      {/* Custom price override — bypasses the auto re-pricing. */}
      <div className={`rounded-lg border px-3 py-2 space-y-2 ${
          customPriceOn ? "border-amber-400/40 bg-amber-500/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
        <label className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs text-white">Use my price (skip auto-pricing)</div>
            <div className="text-[10px] text-white/50">
              {customPriceOn
                ? "Your number sticks even if dates or insurance change. Uncheck to let the system re-price."
                : "Off — the system re-prices from the listed rates whenever you edit."}
            </div>
          </div>
          <input
            type="checkbox"
            data-testid="bo-cal-rental-custom-price-toggle"
            checked={customPriceOn}
            onChange={(e) => setCustomPriceOn(e.target.checked)}
            className="w-5 h-5 accent-amber-400"
          />
        </label>
        {customPriceOn && (
          <label className="block">
            <div className="label-caps text-[9px] text-white/50 mb-1">Total ($)</div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                data-testid="bo-cal-rental-custom-price"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="w-full bg-black/50 border border-amber-400/40 rounded-lg pl-7 pr-3 py-2 text-lg font-semibold text-white outline-none focus:border-amber-300"
              />
            </div>
            {baselineLocked && (
              <div className="text-[10px] text-amber-200/80 mt-1">
                Price is currently locked to <span className="text-amber-100 font-bold">${baselineAmount.toFixed(2)}</span>.
              </div>
            )}
          </label>
        )}
      </div>

      <button
        type="button"
        data-testid="bo-cal-rental-save"
        onClick={save}
        disabled={!dirty || saving}
        className={`w-full py-2.5 rounded-full text-xs label-caps transition ${
          dirty && !saving
            ? "bg-cyan text-midnight font-semibold hover:brightness-110"
            : "bg-white/8 text-white/40 cursor-not-allowed"
        }`}
      >
        {saving ? "Saving…" : dirty ? (customPriceOn ? "Save my price" : "Save & re-price this rental") : "No changes"}
      </button>
    </div>
  );
}

/* ---------------- helpers ---------------- */
function buildMonthGrid(anchor) {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({ date: d });
  }
  const monthLabel = anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return { grid: cells, monthLabel };
}
function bucketByDay(bookings) {
  const buckets = {};
  const add = (key, item) => { (buckets[key] = buckets[key] || []).push(item); };
  for (const b of bookings) {
    try {
      if (b.type === "rental" && b.start_at && b.end_at) {
        const s = new Date(b.start_at);
        const e = new Date(b.end_at);
        const cursor = new Date(s.getFullYear(), s.getMonth(), s.getDate());
        const endDay = new Date(e.getFullYear(), e.getMonth(), e.getDate());
        while (cursor <= endDay) {
          const isStart = sameDay(cursor, s);
          const isEnd = sameDay(cursor, e);
          add(ymd(cursor), { booking: b, type: isStart ? "rental_start" : isEnd ? "rental_end" : "rental_span" });
          cursor.setDate(cursor.getDate() + 1);
        }
      } else if (b.type === "ride" && b.pickup_datetime) {
        add(ymd(new Date(b.pickup_datetime)), { booking: b, type: "ride" });
      }
    } catch (_) {}
  }
  Object.values(buckets).forEach((arr) => arr.sort((a, bb) => {
    if (a.type === "ride" && bb.type !== "ride") return -1;
    if (bb.type === "ride" && a.type !== "ride") return 1;
    const da = new Date(a.booking.pickup_datetime || a.booking.start_at);
    const db = new Date(bb.booking.pickup_datetime || bb.booking.start_at);
    return da - db;
  }));
  return buckets;
}
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function fmtTime(iso) { try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { return ""; } }
function fmtFull(iso) { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } }

/* ---------------- Weather (Open-Meteo, free, no key) ---------------- */
// Simple in-memory cache keyed by "YYYY-MM" so repeat month navigation doesn't refetch.
const WEATHER_CACHE = new Map();
function useWeather(anchor) {
  const [data, setData] = useState({});
  const monthKey = `${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, "0")}`;
  useEffect(() => {
    let cancelled = false;
    if (WEATHER_CACHE.has(monthKey)) { setData(WEATHER_CACHE.get(monthKey)); return; }
    const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const today = new Date(); today.setHours(0,0,0,0);
    // Open-Meteo forecast only covers roughly 16 days ahead; skip past months entirely.
    const maxLead = new Date(today); maxLead.setDate(today.getDate() + 15);
    if (monthEnd < today || monthStart > maxLead) { WEATHER_CACHE.set(monthKey, {}); setData({}); return; }
    const start = monthStart < today ? today : monthStart;
    const end = monthEnd > maxLead ? maxLead : monthEnd;
    const params = new URLSearchParams({
      latitude: "40.6782",  // Brooklyn HQ
      longitude: "-73.9442",
      daily: "weather_code,temperature_2m_max",
      temperature_unit: "fahrenheit",
      timezone: "America/New_York",
      start_date: ymd(start),
      end_date: ymd(end),
    });
    fetch(`https://api.open-meteo.com/v1/forecast?${params}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (cancelled || !json?.daily) return;
        const days = json.daily.time || [];
        const codes = json.daily.weather_code || [];
        const temps = json.daily.temperature_2m_max || [];
        const map = {};
        days.forEach((d, i) => { map[d] = { code: codes[i], tempMax: temps[i], summary: weatherLabel(codes[i]) }; });
        WEATHER_CACHE.set(monthKey, map);
        setData(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [monthKey, anchor]);
  return data;
}
function weatherLabel(code) {
  if (code == null) return "";
  if (code === 0) return "Clear";
  if (code < 3) return "Mostly clear";
  if (code === 3) return "Overcast";
  if (code >= 45 && code < 55) return "Fog";
  if (code >= 51 && code < 60) return "Drizzle";
  if (code >= 61 && code < 70) return "Rain";
  if (code >= 71 && code < 80) return "Snow";
  if (code >= 80 && code < 87) return "Showers";
  if (code >= 95) return "Thunderstorm";
  return "";
}
function WeatherIcon({ code }) {
  if (code == null) return null;
  const cls = "inline-block";
  if (code === 0) return <Sun size={10} className={`${cls} text-yellow-300`} />;
  if (code < 3) return <CloudSun size={10} className={`${cls} text-yellow-200`} />;
  if (code === 3) return <Cloud size={10} className={`${cls} text-white/50`} />;
  if (code >= 71 && code < 80) return <CloudSnow size={10} className={`${cls} text-sky-200`} />;
  if ((code >= 51 && code < 70) || (code >= 80 && code < 87) || code >= 95) return <CloudRain size={10} className={`${cls} text-cyan/70`} />;
  return <Cloud size={10} className={`${cls} text-white/50`} />;
}

/* ---------------- Print daily dispatch manifest ---------------- */
function printDayManifest(day, items) {
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) return;
  const dayLabel = day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const rows = items.map((it) => {
    const b = it.booking;
    const isRental = b.type === "rental";
    return `<tr>
      <td>${isRental ? "Rental" : (fmtTime(b.pickup_datetime) || "—")}</td>
      <td>${escapeHtml(b.vehicle_name || "—")}</td>
      <td>${escapeHtml(b.full_name || "—")}<br/><small>${escapeHtml(b.customer_phone || "")}</small></td>
      <td>${escapeHtml(isRental ? (b.pickup_location || "—") : (b.pickup || "—"))}</td>
      <td>${escapeHtml(isRental ? (b.return_location || "—") : (b.dropoff || "—"))}</td>
      <td>$${Number(b.amount || 0).toFixed(2)}</td>
      <td>${escapeHtml(b.status || "—")}</td>
    </tr>`;
  }).join("");
  w.document.write(`<!doctype html><html><head><title>Manifest · ${dayLabel}</title>
    <style>
      body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:32px;color:#0b1e3b}
      h1{font-size:20px;margin:0 0 4px}
      .sub{color:#64748b;font-size:12px;margin-bottom:20px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th{background:#0b1e3b;color:#fff;padding:8px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.03em}
      td{padding:10px 8px;border-bottom:1px solid #e2e8f0;vertical-align:top}
      small{color:#64748b}
      @media print{@page{margin:0.5in}}
    </style></head><body>
    <h1>Plenty Rydez · Daily Dispatch Manifest</h1>
    <div class="sub">${dayLabel} · ${items.length} reservation${items.length===1?"":"s"} · Printed ${new Date().toLocaleString()}</div>
    <table>
      <thead><tr><th>Time</th><th>Vehicle</th><th>Customer</th><th>Pickup</th><th>Dropoff</th><th>Total</th><th>Status</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <script>window.onload=()=>{window.print();};</script>
    </body></html>`);
  w.document.close();
}
function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]));
}
